<template>
  <div class="dialog-main rounded-xl" v-bind="$attrs">
    <!-- 顶部：光伏概况（占满一行） -->
    <div class="pv-overview">
      <div class="overview-header">
        <div class="overview-title">
          <img src="@/assets/images/station-monitor/image_54.png" width="18" height="18" />
          <span>光伏概况</span>
          <span class="overview-time" v-if="overview.record_time">（采样时间: {{ getSampleTime(overview) }}）</span>
        </div>
      </div>
      <div class="overview-cards">
        <DataCardPropsProvide :value-style="{ fontSize: '24px', fontWeight: 700 }">
          <DataCard title="总有功功率" :value="fmtP.value" :unit="fmtP.unit">
            <template #icon>
              <VChart class="overview-icon" style="width: 64px;height: 64px;" :option="circleOption" />
            </template>
          </DataCard>
          <DataCard title="总无功功率" :value="overview.q ?? '--'" unit="kVar">
            <template #icon>
              <div class="overview-icon" style="background: rgba(43,87,238,0.1)">
                <v-icon color="#2B57EE" size="22">mdi-pulse</v-icon>
              </div>
            </template>
          </DataCard>
          <DataCard title="日发电量" :value="fmtPowerGenD.value" :unit="fmtPowerGenD.unit">
            <template #icon>
              <div class="overview-icon" style="background: rgba(255,152,0,0.12)">
                <v-icon color="#FF9800" size="22">mdi-white-balance-sunny</v-icon>
              </div>
            </template>
            <template #title>
              <div class="d-flex align-center">
                <span>日发电量</span>
                <v-btn class="ml-2" variant="text" color="primary" density="compact">
                  <ChartSpline size="16" @click="onDailyClick" />

                  <v-dialog activator="parent" max-width="640">
                    <template v-slot:default="{ isActive }">
                      <v-card prepend-icon="mdi-picture-in-picture-bottom-right" title="日发电量统计" :loading="dailyLoading">
                        <v-divider class="my-4"></v-divider>
                        <v-form @submit.prevent class="mx-8 mt-4">
                          <v-row>
                            <v-col cols="5">
                              <v-date-input variant="outlined" v-model="dailyStartTime" label="开始日期" density="compact"
                                hide-details />
                            </v-col>
                            <v-col cols="5">
                              <v-date-input variant="outlined" v-model="dailyEndTime" label="结束日期" density="compact"
                                hide-details />
                            </v-col>
                            <v-col cols="2">
                              <v-btn block color="primary" @click="fetchDailyPowerGen" :loading="dailyLoading"
                                rounded="pill">查询</v-btn>
                            </v-col>
                          </v-row>
                        </v-form>
                        <v-card-text>
                          <VChart autoresize :option="dailyEnergyOptions" style="height: 420px;" />
                        </v-card-text>
                        <template v-slot:actions>
                          <v-btn class="ml-auto" color="primary" text="关闭" @click="isActive.value = false"></v-btn>
                        </template>
                      </v-card>
                    </template>
                  </v-dialog>
                </v-btn>
              </div>
            </template>
          </DataCard>
          <DataCard title="总发电量" :value="fmtSumPowerGen.value" :unit="fmtSumPowerGen.unit">
            <template #icon>
              <div class="overview-icon" style="background: rgba(54,192,112,0.12)">
                <v-icon color="#36c070" size="22">mdi-chart-line-variant</v-icon>
              </div>
            </template>
          </DataCard>
          <DataCard title="额定功率" :value="fmtRatedP.value" :unit="fmtRatedP.unit">
            <template #icon>
              <div class="overview-icon" style="background: rgba(255,82,82,0.1)">
                <v-icon color="#FF5252" size="22">mdi-solar-power</v-icon>
              </div>
            </template>
          </DataCard>
        </DataCardPropsProvide>
      </div>
    </div>

    <!-- 功率曲线 + 光伏设备 同一行 -->
    <div class="content-row">
      <!-- 左：功率曲线 -->
      <PowerCurveChart class="curve-col" :station-id="stationId" />

      <!-- 右：光伏设备面板列表 -->
      <div class="left-content">
        <div class="panel-title">
          <img src="@/assets/images/station-monitor/image_54.png" width="18" height="18" />
          <span>光伏设备</span>
          <span class="device-count">
            在线 <span class="count-online">{{ onlineCount }}</span> / {{ realtime.length }}
          </span>
        </div>
        <div class="pv-scroll">
          <div v-if="!realtime || realtime.length === 0" class="empty-text">
            暂无光伏设备
          </div>

          <div v-else class="pv-list">
            <div v-for="(device, index) in realtime" :key="device.uuid || index" class="pv-device-panel"
              :id="'pv-' + (device.uuid ?? index)">

              <!-- 折叠面板标题栏 -->
              <div class="array-header">
                <div class="array-title">
                  <div>
                    <OverflowText :text="device.photovoltaicName || '光伏逆变器'" maxWidth="150px" />
                    <div class="d-flex align-center subtitle-text">
                      <div class="d-flex align-center">{{ device.uuid }}</div>
                      <span variant="text" class="copy-btn" @click="copyText(device.uuid)"
                        style="text-transform: none;">复制</span>
                    </div>
                  </div>
                </div>
                <div class="header-right">
                  <v-chip :color="isOnline(device) ? 'green' : 'red'">
                    {{ isOnline(device) ? '在线' : '离线' }}
                  </v-chip>
                  <span class="header-time" v-if="getSampleTime(device)">
                    采样时间: {{ getSampleTime(device) }}
                  </span>
                </div>
              </div>

              <!-- 展开内容：公共字段 -->

              <transition name="panel-expand">
                <div class="array-content-wrapper">
                  <div class="array-content">
                    <div class="pv-info-card">
                      <DataCardPropsProvide title-position="bottom" :value-style="cardValueStyle">
                        <div class="common-fields-grid">
                          <DataCard class="field-card" title="运行状态" title-position="left"
                            :value="getRealValue(device.runningKey, device.runningValue)" />
                          <DataCard class="field-card" title="有功功率" title-position="left"
                            :value="formatPower(device.power).value" :unit="formatPower(device.power).unit" />
                          <DataCard class="field-card" title="日发电量" title-position="left"
                            :value="formatEnergy(device.DailyPowerGen).value"
                            :unit="formatEnergy(device.DailyPowerGen).unit" />
                        </div>
                      </DataCardPropsProvide>
                    </div>
                  </div>
                </div>
              </transition>

              <!-- 卡片底部操作区 -->
              <div class="panel-footer">
                <v-btn size="small" variant="outlined" color="primary" rounded="pill" block
                  append-icon="mdi-arrow-right" @click.stop="openDetail(device)">
                  详情
                </v-btn>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- 详情抽屉 -->
  <PvDetailDrawer :model-data="modelData" :model-array="modelArray" v-model="detailVisible"
    :device-data="selectedDevice" :stationId="stationId" :is-online="detailIsOnline" />
</template>

<script setup>
import { ref, computed, watch, reactive, shallowRef, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useRealData } from '@/hooks/useRealData'
import dayjs from 'dayjs'
import DataCard from '@/components/DataCard/index.vue'
import DataCardPropsProvide from '@/components/DataCard/PropProvide.vue'
import PvDetailDrawer from './PvDetailDrawer.vue'
import PowerCurveChart from './PowerCurveChart.vue'
import * as api from '@/api/device/pv'
import OverflowText from '@/components/OverflowText/index.js'
import { eui } from 'ems-ui'
import { DailyEnergyOption, CircleOption } from './chart-opt';
import { fmtp, fmte, fmtw } from '@/utils/fortmatter.js'
import { ChartSpline } from 'lucide-vue-next'
import VChart from 'vue-echarts'

const route = useRoute()
const stationId = computed(() => route.query.stationId)
const detailIsOnline = computed(() => {
  return selectedDevice.value?.status == 1
})

// 实时 PV 数据
const { updateOnce } = useRealData({
  initData: [],
  updateFunction: () => {
    return api.getPvDataOverview(stationId.value)
  },

  onUpdate(res) {
    realtime.value = res.data?.realtime || [];
    overview.value = res.data?.overview || {};

    // 更新activePv和record_time
    if (realtime.value.length > 0) {
      activePv.value = realtime.value[0];
    }

  }
})

function getRealValue(key, value) {
  const model = modelData.value[key]
  if (model && model.readConverter) {
    const map = {}
    model.readConverter.split(',').map(item => {
      const [value, label] = item.split(':')
      map[value.trim()] = label.trim()
    })
    return map[value] ?? '--';
  }

  return value ?? '--'
}


// 日发电量弹窗 — 日期选择
const dailyStartTime = shallowRef(dayjs().subtract(7, 'day').toDate())
const dailyEndTime = shallowRef(dayjs().toDate())
const dailyChartData = ref([])
const dailyLoading = ref(false)

function onDailyClick() {
  // 打开弹窗时自动查询一次
  fetchDailyPowerGen()
}

async function fetchDailyPowerGen() {
  if (!dailyStartTime.value || !dailyEndTime.value) return
  dailyLoading.value = true
  await nextTick()
  try {
    const res = await api.getPvDataDailyPowerGeneration({
      stationId: stationId.value,
      start: dayjs(dailyStartTime.value).format('YYYY-MM-DD 00:00:00'),
      end: dayjs(dailyEndTime.value).format('YYYY-MM-DD 23:59:59'),
    })
    dailyChartData.value = res.data || []
  } catch (e) {
    console.error('获取日发电量失败', e)
    dailyChartData.value = []
  } finally {
    dailyLoading.value = false
  }
}

function copyText(text) {
  navigator.clipboard.writeText(text).then(() => {
    eui.showToast('复制成功', 'success')
  }).catch(() => {
    eui.showToast('复制失败', 'error')
  })
}

function formatPower(power) {
  const num = Number(power)
  if (power === null || power === undefined || power === '' || Number.isNaN(num)) {
    return { value: '--', unit: 'W' }
  }
  return fmtw(num * 1000)
}

// 日发电量（kWh），超过 1000 自动换算成 MWh/GWh
function formatEnergy(energy) {
  const num = Number(energy)
  if (energy === null || energy === undefined || energy === '' || Number.isNaN(num)) {
    return { value: '--', unit: 'kWh' }
  }
  return fmte(num)
}

// 详情抽屉
const detailVisible = ref(false)
const selectedDevice = ref(null)

function openDetail(device) {
  selectedDevice.value = device
  detailVisible.value = true
}

// 在线判断
function isOnline(device) {
  return Boolean(device.status)
}

function getSampleTime(device) {
  const time = device.record_time || device.sampTime
  if (!time) return ''
  return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
}

// 在线设备数量
const onlineCount = computed(() => realtime.value.filter((d) => isOnline(d)).length)

const status = reactive({
  model: false,
  realtime: false,
  statistics: false,
})

const overview = ref({});
const realtime = ref([]);
const modelData = ref({});
const modelArray = ref([]);
const activePv = ref({})

const circleOption = computed(() => {
  let percentage = 0;
  if (overview.value.p && overview.value.rated_p) {
    percentage = overview.value.p == 0 ? 0 : (overview.value.p / overview.value.rated_p * 100)
  }
  return CircleOption(percentage);
});

const dailyEnergyOptions = computed(() => {
  return DailyEnergyOption({
    params: {
      start: dailyStartTime.value,
      end: dailyEndTime.value,
    },
    chartData: dailyChartData.value
  })
})

async function fetchPvDataModel(sid) {
  status.model = true;
  const res = await api.getPvDataModel(sid);
  modelData.value = res.data || {};
  modelArray.value = Object.keys(modelData.value).map(key => {
    return modelData.value[key];
  }).sort((a, b) => a.sort - b.sort);
  console.log('modelArray', modelArray.value);
  status.model = false;
}

// 顶部光伏概况：跨设备聚合
const fmtP = computed(() => fmtp(overview.value.p))
const fmtPowerGenD = computed(() => fmte(overview.value.power_gen_d))
const fmtSumPowerGen = computed(() => fmte(overview.value.sum_power_gen))
const fmtRatedP = computed(() => fmtp(overview.value.rated_p))

// 概览更新时间：取所有设备最新采样时间

const cardValueStyle = { fontSize: '16px', fontWeight: 700 }

watch(stationId, (newVal) => {
  if (newVal) {
    updateOnce()
    fetchPvDataModel(newVal)
  }
}, { immediate: true })
</script>

<style scoped lang="scss">
.dialog-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: calc(100vh - var(--header-height) - 137px);
  overflow-y: auto;
  min-width: 0;
  max-width: 100%;
  font-family: 'HarmonyOS Sans SC', sans-serif;
}

.content-row {
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex: 1;
  min-height: 520px;
  min-width: 0;
}

.curve-col {
  flex: 1.3;
  min-width: 0;
}

.left-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  background: #fff;
  border-radius: 20px;
  padding: 16px 22px;
}

/* 各面板统一标题 */
.panel-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 500;
  color: #2e2e2e;
  margin-bottom: 12px;
}

.device-count {
  margin-left: auto;
  font-size: 13px;
  font-weight: 400;
  color: rgba(46, 46, 46, 0.5);

  .count-online {
    font-weight: 600;
    color: var(--color-success);
  }
}

.pv-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.empty-text {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 16px;
}

.pv-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

/* 顶部光伏概况 */
.pv-overview {
  background: #fff;
  border-radius: 20px;
  padding: 16px 22px;
}

.overview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.overview-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 500;
  color: #2e2e2e;
}

.overview-time {
  font-size: 12px;
  font-weight: 400;
  color: #999;
}

.overview-refresh {
  width: 16px;
  height: 16px;
  cursor: pointer;
  opacity: 0.6;
  transition: transform 0.3s ease;

  &:hover {
    transform: rotate(180deg);
    opacity: 1;
  }
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.overview-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: rgba(239, 244, 251, 0.7);
  border-radius: 12px;
}

.overview-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
}

.overview-card-body {
  min-width: 0;
}

.overview-card-title {
  font-size: 13px;
  color: rgba(46, 46, 46, 0.5);
  margin-bottom: 4px;
}

.overview-card-value {
  font-size: 24px;
  font-weight: 700;
  color: #2e2e2e;
  white-space: nowrap;
}

.overview-card-unit {
  font-size: 12px;
  font-weight: 400;
  color: rgba(46, 46, 46, 0.5);
  margin-left: 4px;
}

.pv-device-panel {
  background: #fff;
  border: 1px solid #edf2f6;
  border-radius: 16px;
  overflow: hidden;
}

.highlight-device {
  animation: highlightBlink 0.5s ease-in-out 6;
}

@keyframes highlightBlink {

  0%,
  100% {
    border-color: rgba(244, 247, 252, 1);
    box-shadow: none;
  }

  50% {
    border-color: rgba(58, 104, 255, 1);
    box-shadow: 0 0 12px rgba(58, 104, 255, 0.5);
  }
}

.array-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 18px;
  user-select: none;
  background: #f5f8ff;
}

.array-title {
  font-size: 14px;
  color: rgba(46, 46, 46, 1);
  display: flex;
  align-items: center;
  min-width: 0;
}

.header-icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #FFF5E6 0%, #FFE8CC 100%);
  border-radius: 10px;
  flex-shrink: 0;
}

.title-text {
  font-size: 14px;
  color: #000;
  font-weight: 500;
}

.subtitle-text {
  font-size: 12px;
  color: #444a6d;
  flex-wrap: wrap;
  overflow-wrap: anywhere;
}

.copy-btn {
  font-size: 12px;
  color: var(--color-success);
  margin-left: 8px;
  cursor: pointer;
}

.copy-btn:hover {
  text-decoration: underline;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-shrink: 0;
}

.header-time {
  font-size: 12px;
  color: #666;
}

.detail-btn {
  height: 28px !important;
  font-size: 12px !important;
  padding: 0 12px !important;
  width: 100% !important;
  min-width: 0 !important;
}

.panel-footer {
  display: flex;
  padding: 8px 16px;
  margin-bottom: 8px;
}

.collapse-icon {
  width: 11px;
  height: 6px;
  transition: transform 0.3s ease;

  &.expanded {
    transform: rotate(180deg);
  }
}

.array-content-wrapper {
  padding: 12px 6px 6px;
}

.array-content {
  display: flex;
  gap: 18px;
  align-items: stretch;
  min-width: 0;
}

.pv-info-card {
  flex-shrink: 0;
  flex-grow: 1;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
}

.common-fields-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-card {
  border-radius: 10px;
  padding: 0px 16px;
}

/* Animation */
.panel-expand-enter-active,
.panel-expand-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  opacity: 1;
  overflow: hidden;
}

.panel-expand-enter-from,
.panel-expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

@media (max-width: 1024px) {
  .dialog-main {
    height: auto;
    min-height: 0;
  }

  .content-row {
    flex-direction: column;
  }

  .array-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }
}
</style>
